/**
 * O registry PARCIAL do Financial Planning — spec-011, [T010] / US-1 e US-2.
 *
 * O que a tela de FP já desenha (objetivos, resultado da simulação, projeção
 * patrimonial, links de IPS/formulário) passa a ser desenhável DENTRO da
 * conversa, sem que o `chat-message.vue` saiba que o FP existe: ele resolve a
 * chave que o backend mandou (`ui.component`) no `UI_REGISTRY` e monta. Cartão
 * novo do módulo é uma entrada aqui — nunca uma linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | helpers de unidade | `ia/adaptadores.js` | moeda/data/percentual, compartilhados |
 * | ação de abrir | `ia/acoes.js` | rota e contexto do cliente se resolvem AQUI |
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é snake_case (`valor_total_brl`, `retorno_anual_fracao`) e os componentes
 * reusados da tela falam o vocabulário deles. Apontar direto deixaria o cartão
 * montado e VAZIO, que é a falha calada que este contrato existe para evitar.
 *
 * ## Três decisões que valem explicar
 *
 * 1. **A flag é a do MÓDULO (`FEATURES.FINANCIAL_PLANNING_SIMULATOR`).** É ela
 *    que gateia a rota `fp.home` (`constants/navigation-routes.js:563`), que é
 *    a tela onde esses mesmos números aparecem. O registry é fail-closed na
 *    flag: escritório sem o módulo vê `fallback-card` com `flag_desligada`, em
 *    vez de um dado que ele não contratou.
 * 2. **`states` são os dois de leitura.** `input-available` entra porque é o
 *    estado em que a parte vive enquanto a tool roda: recusá-lo faria aparecer
 *    um cartão de ERRO (`estado_nao_suportado`) no meio de todo turno normal.
 *    Os desfechos de erro ficam de fora — nenhum destes cartões sabe desenhar
 *    uma falha, e o `fallback-card` diz o que aconteceu.
 * 3. **Nada entra no `LEGACY_TOOL_TO_UI`.** As tools declaram `ui` desde a wave
 *    1, então a parte já chega com a chave. Mapear os nomes no mapa legado faria
 *    as mensagens ANTIGAS (persistidas sem `ui`) resolverem para estes cartões
 *    com o `value` da tool no lugar do `ui.props` — outro shape — e a conversa
 *    reaberta trocaria o silêncio de hoje por um `fallback-card` de contrato.
 *
 * ## O `label` é o do BACKEND — e o do link é a EXCEÇÃO
 *
 * `label` é o que o `tool-call-badge` mostra ENQUANTO a tool roda, e por isso é
 * byte a byte o `LABEL` da tool emissora. Três das quatro chaves têm uma tool
 * só, e o `registry.unit.js` compara cada uma com o `.py`.
 *
 * ⚠️ `financial-planning/link` tem DUAS tools emissoras, com rótulos
 * diferentes (`'Gerando link de IPS...'` e `'Gerando link de formulário...'`):
 * não existe string que seja byte a byte as duas. O rótulo daqui é o PREFIXO
 * comum, e quem diz qual link está sendo gerado é o `label` da PARTE, que o
 * badge lê ANTES deste (passo 0 do `tool-call-badge`, fechado na [T018] da
 * spec-010). Escolher um dos dois aqui faria o badge anunciar "IPS" enquanto a
 * tool do formulário roda — e o teste trava o prefixo, não a escolha.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

/** A chave do módulo no catálogo de navegação. */
const MODULO = ROUTE_KEYS.FP_HOME

/** A flag do módulo — ver a decisão 1 no cabeçalho. */
const FLAG_DO_MODULO = FEATURES.FINANCIAL_PLANNING_SIMULATOR

// Enquanto a tool roda, e quando o resultado chega.
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato.
 *
 * Exportadas porque quem lê o registry e quem escreve o teste precisam da MESMA
 * string: uma chave redigitada não quebra nada visivelmente — o cartão
 * simplesmente não aparece.
 */
export const CHAVES = {
  OBJETIVOS: 'financial-planning/objetivos',
  SIMULACAO: 'financial-planning/simulacao-principal',
  PROJECAO: 'financial-planning/projecao',
  LINK: 'financial-planning/link',
}

/**
 * A chave cujo rótulo é compartilhado por mais de uma tool — ver o cabeçalho.
 * O teste lê daqui em vez de repetir a string.
 */
export const CHAVE_DE_ROTULO_COMPARTILHADO = CHAVES.LINK

export default {
  [CHAVES.OBJETIVOS]: {
    component: () => import('@modules/financial-planning/ia/cartoes/objetivos'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-financial-planning-objetivos.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando objetivos financeiros...',
  },
  [CHAVES.SIMULACAO]: {
    component: () =>
      import('@modules/financial-planning/ia/cartoes/simulacao-principal'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-financial-planning-simulacao-principal.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando simulação principal...',
  },
  [CHAVES.PROJECAO]: {
    component: () => import('@modules/financial-planning/ia/cartoes/projecao'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-financial-planning-projecao.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando projeção patrimonial...',
  },
  [CHAVES.LINK]: {
    component: () => import('@modules/financial-planning/ia/cartoes/link'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-financial-planning-link.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    // O prefixo comum às duas tools emissoras — ver a exceção no cabeçalho.
    label: 'Gerando link...',
  },
}
